import React from 'react';
import { Helmet } from 'react-helmet-async';
import ClockDisplay from '../components/ClockDisplay';
import VideoAlarm from '../components/VideoAlarm';
import { useFullscreen } from '../contexts/FullscreenContext';
import { getCanonicalUrl } from '../utils/seoUtils';

export default function VideoAlarmPage({ currentTime, videoAlarm }) {
  const { isFullscreen } = useFullscreen();
  
  const {
    alarmHour,
    alarmMinute,
    snoozeMinutes,
    selectedMelody,
    growing,
    alarmActive,
    alarmRinging,
    timeLeft,
    setAlarmHour,
    setAlarmMinute,
    setSnoozeMinutes,
    setSelectedMelody,
    setGrowing,
    setAlarmActive,
    stopAlarm,
    snoozeAlarm,
    testSound,
    // 视频相关 props
    youtubeUrl,
    setYoutubeUrl,
    videoId,
    setVideoId,
    volume,
    setVolume,
    ytApiReady,
    showPlayPrompt,
    audioUnlocked,
    popularVideos,
    unlockAudio,
    playAlertSound,
    createYouTubePlayer,
    manualStartPlay,
    stopVideo,
    extractVideoId
  } = videoAlarm;

  return (
    <>
      <Helmet>
        <title>Video Alarm Clock Online - YouTube Alarm | Clock Online</title>
        <meta name="description" content="Set video alarms with YouTube videos. Free online video alarm clock that plays your favorite videos when it's time to wake up. Customize your morning routine." />
        <link rel="canonical" href={getCanonicalUrl('/video-alarm')} />
        <meta property="og:title" content="Video Alarm Clock Online - YouTube Alarm | Clock Online" />
        <meta property="og:description" content="Set video alarms with YouTube videos. Free online video alarm clock that plays your favorite videos when it's time to wake up." />
        <meta property="og:url" content={getCanonicalUrl('/video-alarm')} />
        <meta name="twitter:title" content="Video Alarm Clock Online - YouTube Alarm | Clock Online" />
        <meta name="twitter:description" content="Set video alarms with YouTube videos. Free online video alarm clock." />
      </Helmet>
      {!isFullscreen && (
        <h2 className="text-4xl font-bold text-white text-center mb-8">
          Video Alarm Clock
        </h2>
      )}
      <ClockDisplay currentTime={currentTime} showFullscreenButton={false} />
      
      {/* 全屏模式下的声音控制界面 */}
      {isFullscreen && alarmRinging && (
        <div className="space-y-6">
          {/* 全屏模式下的音频控制界面 */}
          <div className="bg-gradient-to-br from-purple-600/40 to-blue-600/40 backdrop-blur-md rounded-3xl p-8 border border-purple-400/30 shadow-2xl">
            <div className="text-center">
              <div className="w-24 h-24 mx-auto mb-6 bg-white/20 rounded-full flex items-center justify-center">
                 <div className="w-16 h-16 bg-gradient-to-r from-purple-400 to-blue-400 rounded-full flex items-center justify-center animate-pulse">
                   <svg className="w-8 h-8 text-white animate-bounce" fill="currentColor" viewBox="0 0 20 20">
                     <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.617.816L4.846 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.846l3.537-3.816a1 1 0 011.617.816zM16 8a2 2 0 11-4 0 2 2 0 014 0zm-2 6a4 4 0 100-8 4 4 0 000 8z" clipRule="evenodd" />
                   </svg>
                 </div>
               </div>
               
               <h3 className="text-3xl font-bold text-white mb-2 animate-fade-in">视频闹钟响起</h3>
               <p className="text-white/80 mb-6 animate-fade-in">正在播放闹钟音效</p>
              
              {/* 音量显示 */}
              <div className="mb-6">
                <p className="text-white/70 text-sm mb-2">音量: {volume}%</p>
                <div className="w-full bg-white/20 rounded-full h-2">
                  <div 
                    className="bg-gradient-to-r from-purple-400 to-blue-400 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${volume}%` }}
                  ></div>
                </div>
              </div>
              
              {/* 视频信息 */}
              {videoId && (
                <div className="mb-6 p-4 bg-white/10 rounded-xl">
                  <p className="text-white/80 text-sm mb-2">设定的视频:</p>
                  <p className="text-white font-medium">{youtubeUrl}</p>
                  <p className="text-white/60 text-xs mt-2">
                    💡 全屏模式下仅播放音效，退出全屏可观看视频
                  </p>
                </div>
              )}
            </div>
          </div>
          
          {/* 全屏模式下的控制按钮 */}
          <div className="flex gap-4 justify-center">
            <button
              onClick={stopAlarm}
              className="px-8 py-4 bg-red-600 hover:bg-red-700 text-white rounded-xl font-bold text-lg transition-colors shadow-lg flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8 7a1 1 0 00-1 1v4a1 1 0 001 1h4a1 1 0 001-1V8a1 1 0 00-1-1H8z" clipRule="evenodd" />
              </svg>
              停止闹钟
            </button>
            <button
              onClick={snoozeAlarm}
              className="px-8 py-4 bg-yellow-600 hover:bg-yellow-700 text-white rounded-xl font-bold text-lg transition-colors shadow-lg flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
              </svg>
              贪睡 ({snoozeMinutes} 分钟)
            </button>
          </div>
        </div>
      )}
      
      {/* 非全屏模式下的完整界面 */}
      {!isFullscreen && (
        <>
          <VideoAlarm
            alarmHour={alarmHour}
            alarmMinute={alarmMinute}
            snoozeMinutes={snoozeMinutes}
            selectedMelody={selectedMelody}
            growing={growing}
            alarmActive={alarmActive}
            alarmRinging={alarmRinging}
            timeLeft={timeLeft}
            setAlarmHour={setAlarmHour}
            setAlarmMinute={setAlarmMinute}
            setSnoozeMinutes={setSnoozeMinutes}
            setSelectedMelody={setSelectedMelody}
            setGrowing={setGrowing}
            setAlarmActive={setAlarmActive}
            stopAlarm={stopAlarm}
            snoozeAlarm={snoozeAlarm}
            testSound={testSound}
            // 视频相关 props
            youtubeUrl={youtubeUrl}
            setYoutubeUrl={setYoutubeUrl}
            videoId={videoId}
            setVideoId={setVideoId}
            volume={volume}
            setVolume={setVolume}
            ytApiReady={ytApiReady}
            showPlayPrompt={showPlayPrompt}
            audioUnlocked={audioUnlocked}
            popularVideos={popularVideos}
            unlockAudio={unlockAudio}
            playAlertSound={playAlertSound}
            createYouTubePlayer={createYouTubePlayer}
            manualStartPlay={manualStartPlay}
            stopVideo={stopVideo}
            extractVideoId={extractVideoId}
          />
          
          {/* Description Section */}
          <div className="mt-16 max-w-4xl mx-auto px-6 text-white">
            <h2 className="text-3xl font-bold text-center mb-8 text-blue-300">
              Complete Guide to Video Alarm Clock Online
            </h2>
            
            <div className="space-y-8 text-lg leading-relaxed">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <h3 className="text-2xl font-semibold mb-4 text-cyan-500">▶ How to Set Up Video Alarm Clock</h3>
                <p className="mb-4">
                  To set a video alarm clock on the website, select the time (hours and minutes) and adjust the snooze length (the period to reset the alarm). Enter the name of a video or a music clip that you want to play on YouTube. The system will automatically suggest a video that can be used as an alarm signal. Turn on the alarm. The video will start playing at the set time.
                </p>
                <p className="mb-4">
                  Our Video Alarm Clock Online provides an intuitive interface that makes setting up your wake-up experience simple and enjoyable. Whether you prefer energetic music videos, calming nature scenes, or motivational content, you can customize your alarm to match your personal preferences and mood.
                </p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <h3 className="text-2xl font-semibold mb-4 text-cyan-500">▶ Internet Connection Requirements</h3>
                <p className="mb-4">
                  An active Internet connection is required for the online video alarm clock. Test the quality of your internet connection before setting your alarm to ensure smooth video playback. A stable connection will guarantee that your Video Alarm Clock Online works reliably and wakes you up on time with your chosen video content.
                </p>
                <p className="mb-4">
                  The Video Alarm Clock Online streams videos directly from YouTube, so a consistent internet connection is essential. We recommend checking your connection speed and stability before going to sleep, especially if you're using the Video Alarm Clock Online for important appointments or events.
                </p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <h3 className="text-2xl font-semibold mb-4 text-cyan-500"><span className="text-yellow-400">💡</span> Important Usage Tips</h3>
                <p className="mb-4">
                  Do not close the current tab or the web-browser itself. Do not shut down your personal computer. Otherwise, the online video alarm clock will not work. Turn off the Power Save mode on your personal computer so the video alarm clock could be triggered in time.
                </p>
                <p className="mb-4">
                  For the Video Alarm Clock Online to function properly, your device must remain active and connected. We strongly recommend disabling sleep mode, screen savers, and any power-saving features that might interrupt the Video Alarm Clock Online. Keep your browser tab active and ensure your computer remains powered on throughout the night.
                </p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <h3 className="text-2xl font-semibold mb-4 text-cyan-500">▶ Safety and Sound Recommendations</h3>
                <p className="mb-4">
                  Do not sleep in headphones plugged to a computer. They might fall off your head and you will not hear the sound. Increase speakers' volume to make sure you will hear the video alarm clock. Position your speakers at an appropriate distance where the Video Alarm Clock Online can be clearly heard but won't be too jarring.
                </p>
                <p className="mb-4">
                  To ensure the Video Alarm Clock Online effectively wakes you up, test the volume level before going to sleep. Set your speakers to a volume that is audible but comfortable. Avoid using headphones as they can easily disconnect or fall off during sleep, potentially causing you to miss your Video Alarm Clock Online wake-up call.
                </p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <h3 className="text-2xl font-semibold mb-4 text-cyan-500">▶ Share Your Experience</h3>
                <p className="mb-4">
                  Did you like using YouTube videos as your alarm signal? Share it with friends and leave your opinion in comments. The Video Alarm Clock Online community is growing, and we love hearing about how our platform has improved your morning routine. Your feedback helps us enhance the Video Alarm Clock Online experience for everyone.
                </p>
                <p className="mb-4">
                  Join thousands of satisfied users who have discovered the power of waking up to their favorite videos with the Video Alarm Clock Online. Whether you're sharing your favorite wake-up videos or providing suggestions for improvements, your input makes the Video Alarm Clock Online better for all users.
                </p>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}